// ABOUTME: Normalizes rich HTML before editors that discard literal line breaks process it.
// ABOUTME: Preserves semantic word boundaries while leaving explicit HTML elements unchanged.

const WHITESPACE_SENSITIVE_ELEMENTS = new Set(["pre"]);
const RAW_TEXT_ELEMENTS = new Set(["textarea", "script", "style"]);
const VOID_ELEMENTS = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr"
]);
const OFFICE_HTML_RE = /(?:\bclass=(?:"|')?Mso|\bmso-|<o:p\b|<\/?o:|<\/?w:)/i;
const DIV_STYLE_ALLOWLIST = new Set([
    "margin-top",
    "margin-bottom",
    "margin-left",
    "margin-right",
    "text-indent",
    "text-align",
    "line-height",
    "direction",
    "unicode-bidi",
    "word-break",
    "width",
    "height",
    "font-family",
    "font-size",
    "font-weight",
    "font-style",
    "color",
    "background-color",
    "text-decoration",
    "padding",
    "padding-top",
    "padding-right",
    "padding-bottom",
    "padding-left"
]);

const normalizeTextNode = (text: string) => text.replace(/\r\n?|\n/g, " ");

const findStyleAttribute = (tag: string) => {
    let index = 1;
    while (/\s/.test(tag[index] || "")) index += 1;
    while (index < tag.length && !/[\s/>]/.test(tag[index])) index += 1;

    while (index < tag.length) {
        const attributeStart = index;
        while (/\s/.test(tag[index] || "")) index += 1;
        if (!tag[index] || tag[index] === ">" || tag[index] === "/") {
            return undefined;
        }
        const nameStart = index;
        while (index < tag.length && !/[\s=/>]/.test(tag[index])) {
            index += 1;
        }
        const name = tag.slice(nameStart, index).toLowerCase();
        while (/\s/.test(tag[index] || "")) index += 1;

        let value = "";
        if (tag[index] === "=") {
            index += 1;
            while (/\s/.test(tag[index] || "")) index += 1;
            const quote =
                tag[index] === '"' || tag[index] === "'"
                    ? tag[index]
                    : undefined;
            if (quote) {
                index += 1;
                const valueStart = index;
                while (index < tag.length && tag[index] !== quote) index += 1;
                value = tag.slice(valueStart, index);
                if (tag[index] === quote) index += 1;
            } else {
                const valueStart = index;
                while (index < tag.length && !/[\s>]/.test(tag[index])) {
                    index += 1;
                }
                value = tag.slice(valueStart, index);
            }
        }
        if (name === "style") {
            return {
                attribute: tag.slice(attributeStart, index),
                value
            };
        }
    }
    return undefined;
};

type WhitespaceSensitivity = boolean | "inherit";

const findWhitespaceSensitivity = (
    tag: string
): WhitespaceSensitivity | undefined => {
    const style = findStyleAttribute(tag)?.value;
    if (!style) return undefined;

    let selected:
        { important: boolean; sensitivity: WhitespaceSensitivity } | undefined;
    for (const declaration of style.split(";")) {
        const colonIndex = declaration.indexOf(":");
        if (colonIndex < 0) continue;
        const property = declaration.slice(0, colonIndex).trim().toLowerCase();
        if (property !== "white-space") continue;

        const rawValue = declaration.slice(colonIndex + 1).trim();
        const important = /\s*!important\s*$/i.test(rawValue);
        const value = rawValue
            .replace(/\s*!important\s*$/i, "")
            .trim()
            .toLowerCase();
        const sensitivity: WhitespaceSensitivity | undefined = [
            "pre",
            "pre-wrap",
            "pre-line",
            "break-spaces"
        ].includes(value)
            ? true
            : ["normal", "nowrap", "initial"].includes(value)
              ? false
              : ["inherit", "unset"].includes(value)
                ? "inherit"
                : undefined;
        if (sensitivity === undefined || (selected?.important && !important)) {
            continue;
        }
        selected = { important, sensitivity };
    }
    return selected?.sensitivity;
};

const isWhitespaceSensitiveElement = (
    tag: string,
    elementName: string,
    inherited: boolean
) => {
    const inlineSensitivity = findWhitespaceSensitivity(tag);
    if (inlineSensitivity === "inherit") return inherited;
    if (inlineSensitivity !== undefined) return inlineSensitivity;
    return WHITESPACE_SENSITIVE_ELEMENTS.has(elementName) || inherited;
};

const findTagEnd = (html: string, tagStart: number) => {
    let quote: '"' | "'" | null = null;
    for (let index = tagStart + 1; index < html.length; index += 1) {
        const character = html[index];
        if (quote) {
            if (character === quote) quote = null;
        } else if (character === '"' || character === "'") {
            quote = character;
        } else if (character === ">") {
            return index + 1;
        }
    }
    return -1;
};

const findProtectedElementEnd = (
    html: string,
    contentStart: number,
    elementName: string
) => {
    const findRawTextElementEnd = (start: number, rawElementName: string) => {
        const closingTag = new RegExp(`</${rawElementName}\\s*>`, "i").exec(
            html.slice(start)
        );
        return closingTag?.index === undefined
            ? -1
            : start + closingTag.index + closingTag[0].length;
    };
    if (RAW_TEXT_ELEMENTS.has(elementName)) {
        return findRawTextElementEnd(contentStart, elementName);
    }

    let depth = 1;
    let index = contentStart;
    while (index < html.length) {
        const tagStart = html.indexOf("<", index);
        if (tagStart < 0) return -1;
        if (html.startsWith("<!--", tagStart)) {
            const commentEnd = html.indexOf("-->", tagStart + 4);
            if (commentEnd < 0) return -1;
            index = commentEnd + 3;
            continue;
        }
        const tagEnd = findTagEnd(html, tagStart);
        if (tagEnd < 0) return -1;
        const tag = html.slice(tagStart, tagEnd);
        const closingName = tag
            .match(/^<\s*\/\s*([a-z][\w:-]*)\b/i)?.[1]
            ?.toLowerCase();
        const openingName = tag
            .match(/^<\s*([a-z][\w:-]*)\b/i)?.[1]
            ?.toLowerCase();
        if (closingName === elementName) {
            depth -= 1;
            if (depth === 0) return tagEnd;
        } else if (openingName === elementName && !/\/\s*>$/.test(tag)) {
            depth += 1;
        }
        if (
            openingName &&
            RAW_TEXT_ELEMENTS.has(openingName) &&
            !/\/\s*>$/.test(tag)
        ) {
            const rawTextEnd = findRawTextElementEnd(tagEnd, openingName);
            if (rawTextEnd < 0) return -1;
            index = rawTextEnd;
            continue;
        }
        index = tagEnd;
    }
    return -1;
};

export const normalizeRichTextHtmlLineBreaks = (html: string) => {
    let normalized = "";
    let index = 0;

    while (index < html.length) {
        const tagStart = html.indexOf("<", index);
        if (tagStart < 0) {
            return normalized + normalizeTextNode(html.slice(index));
        }
        normalized += normalizeTextNode(html.slice(index, tagStart));

        if (html.startsWith("<!--", tagStart)) {
            const commentEnd = html.indexOf("-->", tagStart + 4);
            if (commentEnd < 0) return normalized + html.slice(tagStart);
            normalized += html.slice(tagStart, commentEnd + 3);
            index = commentEnd + 3;
            continue;
        }

        const tagEnd = findTagEnd(html, tagStart);
        if (tagEnd < 0) {
            return normalized + html.slice(tagStart);
        }
        const tag = html.slice(tagStart, tagEnd);
        normalized += tag;
        index = tagEnd;

        const openingTag = tag.match(/^<\s*([a-z][\w:-]*)\b/i);
        const elementName = openingTag?.[1]?.toLowerCase();
        if (
            !elementName ||
            VOID_ELEMENTS.has(elementName) ||
            (!RAW_TEXT_ELEMENTS.has(elementName) &&
                !isWhitespaceSensitiveElement(tag, elementName, false)) ||
            /\/\s*>$/.test(tag)
        ) {
            continue;
        }

        const protectedEnd = findProtectedElementEnd(html, index, elementName);
        if (protectedEnd < 0) {
            return normalized + html.slice(index);
        }
        normalized += html.slice(index, protectedEnd);
        index = protectedEnd;
    }

    return normalized;
};

const replaceWhitespaceSensitiveLineBreaksWithHtmlBreaks = (html: string) => {
    let prepared = "";
    let index = 0;
    const elementStack: Array<{ name: string; whitespaceSensitive: boolean }> =
        [{ name: "", whitespaceSensitive: false }];
    const prepareText = (text: string) =>
        text.replace(
            /\r\n?|\n/g,
            elementStack[elementStack.length - 1].whitespaceSensitive
                ? "<br>"
                : " "
        );

    while (index < html.length) {
        const tagStart = html.indexOf("<", index);
        if (tagStart < 0) return prepared + prepareText(html.slice(index));
        prepared += prepareText(html.slice(index, tagStart));

        if (html.startsWith("<!--", tagStart)) {
            const commentEnd = html.indexOf("-->", tagStart + 4);
            if (commentEnd < 0) return prepared + html.slice(tagStart);
            prepared += html.slice(tagStart, commentEnd + 3);
            index = commentEnd + 3;
            continue;
        }

        const tagEnd = findTagEnd(html, tagStart);
        if (tagEnd < 0) return prepared + html.slice(tagStart);
        const tag = html.slice(tagStart, tagEnd);
        prepared += tag;
        index = tagEnd;

        const closingName = tag
            .match(/^<\s*\/\s*([a-z][\w:-]*)\b/i)?.[1]
            ?.toLowerCase();
        if (closingName) {
            let matchingIndex = elementStack.length - 1;
            while (
                matchingIndex > 0 &&
                elementStack[matchingIndex].name !== closingName
            ) {
                matchingIndex -= 1;
            }
            if (matchingIndex > 0) elementStack.length = matchingIndex;
            continue;
        }

        const openingName = tag
            .match(/^<\s*([a-z][\w:-]*)\b/i)?.[1]
            ?.toLowerCase();
        if (
            !openingName ||
            VOID_ELEMENTS.has(openingName) ||
            /\/\s*>$/.test(tag)
        ) {
            continue;
        }

        if (RAW_TEXT_ELEMENTS.has(openingName)) {
            const rawTextEnd = findProtectedElementEnd(
                html,
                index,
                openingName
            );
            if (rawTextEnd < 0) return prepared + html.slice(index);
            prepared += html.slice(index, rawTextEnd);
            index = rawTextEnd;
            continue;
        }

        elementStack.push({
            name: openingName,
            whitespaceSensitive: isWhitespaceSensitiveElement(
                tag,
                openingName,
                elementStack[elementStack.length - 1].whitespaceSensitive
            )
        });
    }

    return prepared;
};

const rewriteOpeningTags = (
    html: string,
    elementName: string,
    rewrite: (tag: string) => string
) => {
    let rewritten = "";
    let index = 0;
    while (index < html.length) {
        const tagStart = html.indexOf("<", index);
        if (tagStart < 0) return rewritten + html.slice(index);
        rewritten += html.slice(index, tagStart);

        if (html.startsWith("<!--", tagStart)) {
            const commentEnd = html.indexOf("-->", tagStart + 4);
            if (commentEnd < 0) return rewritten + html.slice(tagStart);
            rewritten += html.slice(tagStart, commentEnd + 3);
            index = commentEnd + 3;
            continue;
        }

        const tagEnd = findTagEnd(html, tagStart);
        if (tagEnd < 0) return rewritten + html.slice(tagStart);
        const tag = html.slice(tagStart, tagEnd);
        const openingName = tag
            .match(/^<\s*([a-z][\w:-]*)\b/i)?.[1]
            ?.toLowerCase();
        rewritten += openingName === elementName ? rewrite(tag) : tag;
        index = tagEnd;

        if (
            openingName &&
            RAW_TEXT_ELEMENTS.has(openingName) &&
            !/\/\s*>$/.test(tag)
        ) {
            const rawTextEnd = findProtectedElementEnd(
                html,
                index,
                openingName
            );
            if (rawTextEnd < 0) return rewritten + html.slice(index);
            rewritten += html.slice(index, rawTextEnd);
            index = rawTextEnd;
        }
    }
    return rewritten;
};

export const sanitizeOfficeHtml = (html: string) => {
    const normalizedHtml = normalizeRichTextHtmlLineBreaks(html);
    if (!normalizedHtml || !OFFICE_HTML_RE.test(normalizedHtml)) {
        return normalizedHtml;
    }

    const stripOfficeStyles = (styleValue: string) =>
        styleValue
            .split(";")
            .map((item) => item.trim())
            .filter(Boolean)
            .filter((item) => {
                const prop = item.split(":")[0]?.trim().toLowerCase();
                if (!prop) return false;
                if (prop.startsWith("mso-")) return false;
                if (prop === "language") return false;
                if (prop === "punctuation-wrap") return false;
                if (prop === "text-justify") return false;
                return DIV_STYLE_ALLOWLIST.has(prop);
            })
            .join("; ");

    const withoutOfficeTags = normalizedHtml.replace(
        /<o:p\b[^>]*>[\s\S]*?<\/o:p>/gi,
        ""
    );
    const sanitizedHtml = rewriteOpeningTags(
        withoutOfficeTags,
        "div",
        (tag) => {
            const style = findStyleAttribute(tag);
            if (!style) return tag;
            const sanitizedStyle = stripOfficeStyles(style.value);
            if (!sanitizedStyle) {
                return tag.replace(style.attribute, "");
            }
            return tag.replace(style.attribute, ` style="${sanitizedStyle}"`);
        }
    );

    return normalizeRichTextHtmlLineBreaks(sanitizedHtml);
};

export const prepareRichTextHtmlForEditor = (html: string) =>
    replaceWhitespaceSensitiveLineBreaksWithHtmlBreaks(
        sanitizeOfficeHtml(html)
    );
